import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package } from "lucide-react";
import { api, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

export default function MyOrders() {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);

  return (
    <div className="relative z-10 pt-32 pb-24 max-w-4xl mx-auto px-6 md:px-10">
      <h1 className="font-serif text-5xl md:text-7xl font-light mb-12" data-testid="my-orders-title">I miei ordini</h1>

      {orders === null && <p className="text-muted-foreground">Caricamento…</p>}

      {orders && orders.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-6 text-muted-foreground" data-testid="no-orders">
          <Package size={44} strokeWidth={1} />
          <p>Non hai ancora ordinato nessun incanto.</p>
          <Link to="/shop"><Button className="rounded-full bg-gold hover:bg-gold/90 text-white">Scopri la collezione</Button></Link>
        </div>
      )}

      <div className="space-y-8">
        {orders && orders.map((o, idx) => (
          <motion.div
            key={o.id}
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.06 }}
            className="border border-border rounded-lg p-6 bg-card"
            data-testid={`order-${o.id}`}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5 pb-5 border-b border-border">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Ordine · {formatDate(o.created_at)}</p>
                <p className="font-serif text-2xl mt-1" data-testid={`order-total-${o.id}`}>{eur(o.total)}</p>
              </div>
              <span className="text-xs bg-primary/15 text-primary px-3 py-1.5 rounded-full capitalize" data-testid={`order-status-${o.id}`}>{o.status}</span>
            </div>
            {o.tracking_code && (
              <div className="mb-5 flex flex-wrap items-center gap-2 text-sm bg-secondary/50 border border-border rounded-md px-4 py-3" data-testid={`order-tracking-${o.id}`}>
                <span className="text-muted-foreground">Spedito{o.carrier_name ? ` con ${o.carrier_name}` : ""}:</span>
                <span className="font-medium tracking-wide text-gold">{o.tracking_code}</span>
                {o.tracking_url && (
                  <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="ml-auto rounded-full bg-gold hover:bg-gold/90 text-white text-xs px-4 py-1.5 transition-colors" data-testid={`track-link-${o.id}`}>
                    Traccia il pacco →
                  </a>
                )}
              </div>
            )}
            <div className="space-y-4">
              {(o.items || []).map((it, i) => (
                <div key={i} className="flex items-center gap-4">
                  <img src={it.image} alt="" className="h-16 w-14 object-cover rounded" />
                  <div className="flex-1">
                    <p className="font-serif text-lg leading-tight">{it.name}</p>
                    <p className="text-sm text-muted-foreground">Quantità: {it.quantity}</p>
                  </div>
                  <span className="text-sm">{eur(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            {o.shipping_cost ? (
              <div className="mt-4 pt-4 border-t border-border text-sm flex justify-between text-muted-foreground" data-testid={`order-shipping-${o.id}`}>
                <span>{o.shipping_option || "Spedizione"}</span>
                <span data-testid={`order-shipping-cost-${o.id}`}>{eur(o.shipping_cost)}</span>
              </div>
            ) : null}
          </motion.div>
        ))}
      </div>
    </div>
  );
}
